// One step as a section of the survey page (redesign spec §7): read-only
// until Edit, then that step's fields alone. Its form still carries the
// whole list — a save always PUTs every step with this one replaced, and
// the cross-step rules (one score, split thresholds) need the rest — so it
// re-seeds from the saved list whenever a sibling's save or a move changes
// it while this card isn't editing.

import { Badge, Button, Confirm, toast } from "@pej/kore/components";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Pencil } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "@/api/error";
import { usePutSteps, useStepsSaving } from "@/api/surveys";
import type { StepKind, Survey } from "@/api/types";
import { DangerStrip } from "@/components/section-page/danger-zone";
import { SectionCard } from "@/components/section-page/section-card";
import { useSectionEdit } from "@/components/section-page/section-edit";
import { useAssetClientContext } from "@/features/assets/asset-client-context";
import { Form, useForm } from "@/lib/forms";
import { countOf } from "@/lib/plural";
import { KIND_LABELS } from "../step-kinds";
import { newStep, type StepsFormValues, toPutSteps, toStepsDefaults, validateSteps } from "../survey-schema";
import { SECTION } from "../survey-sections";
import { resolveStepAssets } from "./step-assets";
import { StepFields } from "./step-fields";
import { describeStepsProblem, serverStepsProblem, splitFollows, stepPrompt } from "./step-list";
import { StepSummary } from "./step-summary";
import { toastStepsError } from "./toast-steps-error";
import { reconcileSplitThresholds, useStepsRules } from "./use-steps-rules";

export interface StepSectionProps {
    survey: Survey;
    stepId: string | null;
    draftKind?: StepKind;
    onDraftDone?: () => void;
    onLive: (steps: StepsFormValues | null) => void;
    /** View-mode ↑/↓, run by StepsSection (absent for a draft, and while
     *  another step is editing — the buttons show disabled then). */
    onMove?: (direction: "up" | "down") => void;
    /** Runs the removal: resolves to the refusal message, or null once deleted (absent for a draft). */
    onDelete?: () => Promise<string | null>;
}

export function StepSection({ survey, stepId, draftKind, onDraftDone, onLive, onMove, onDelete }: StepSectionProps) {
    const saved = useMemo(() => toStepsDefaults(survey.steps).steps, [survey.steps]);
    const isDraft = stepId === null;
    // Seeded already reconciled: useStepsRules would otherwise move a stored
    // off-scale split threshold on mount with shouldDirty, and the card
    // would read as edited before anyone touched it.
    const baseList = useMemo(
        () => reconcileSplitThresholds(isDraft && draftKind ? [...saved, newStep(draftKind)] : saved),
        [isDraft, draftKind, saved],
    );
    const index = isDraft ? saved.length : saved.findIndex((step) => step.stepId === stepId);
    const row = survey.steps.find((step) => step.id === stepId) ?? null;
    const sectionId = isDraft ? SECTION.newStep : SECTION.step(stepId);
    const form = useForm<StepsFormValues>({ defaultValues: { steps: baseList } });
    const rules = useStepsRules(form);
    const putSteps = usePutSteps(survey.workspaceId, survey.id);
    // Edit seeds this card from the saved list, so it waits for a move that's still landing.
    const stepsSaving = useStepsSaving(survey.id);
    const assetCtx = useAssetClientContext();
    const queryClient = useQueryClient();
    const [problem, setProblem] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    // From the SAVED step, never the form: useSectionEdit re-registers when
    // the title changes, and re-registering drops the section out of edit
    // mode — a live title would end the edit on the first keystroke. The
    // card and nav titles catch up on save.
    const title = isDraft ? "New step" : `${index + 1} · ${row ? stepPrompt(row) : "Untitled"}`;
    // Spec §7: turning on "Use as score" here takes it off the step that has it.
    const savedScoreAt = saved.findIndex((step) => step.isMetric);
    const scoreMovesFrom =
        rules.values.steps[index]?.isMetric === true && savedScoreAt >= 0 && savedScoreAt !== index
            ? savedScoreAt + 1
            : null;

    const edit = useSectionEdit({
        sectionId,
        sectionTitle: title,
        isDirty: isDraft || form.formState.isDirty,
        hasErrors: problem !== null,
        reset: () => {
            form.reset({ steps: baseList });
            setProblem(null);
            if (isDraft) onDraftDone?.();
        },
    });

    // Re-seed from the saved list whenever it moves and this card isn't
    // editing: its form carries every step, and a stale copy of a sibling
    // would be PUT back on the next save.
    // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the saved list
    useEffect(() => {
        if (!edit.isEditing) form.reset({ steps: baseList });
    }, [baseList, edit.isEditing]);

    const cardRef = useRef<HTMLElement>(null);
    // A draft opens in edit mode, and is brought into view: it's added at the
    // end of its list, often below the fold. scroll-mt on the section lands it
    // clear of the compact header.
    // biome-ignore lint/correctness/useExhaustiveDependencies: mount only
    useEffect(() => {
        if (!isDraft) return;
        edit.enterEdit();
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    // Live values go out only while editing, and the edit's end — leaving
    // edit mode or unmounting mid-edit (a draft's Cancel, a delete, a saved
    // draft) — clears them in the cleanup. React runs every cleanup before
    // any effect body, so a card leaving edit never overwrites the values of
    // the card that took over; and a card that isn't editing never clears
    // anyone's.
    useEffect(() => {
        if (!edit.isEditing) return;
        onLive(form.getValues());
        const subscription = form.watch(() => onLive(form.getValues()));
        return () => {
            subscription.unsubscribe();
            onLive(null);
        };
    }, [edit.isEditing, form, onLive]);

    async function save() {
        setProblem(null);
        const values = form.getValues();
        const resolved = await resolveStepAssets(values.steps, saved, assetCtx, queryClient);
        if (!resolved.ok) {
            setProblem(resolved.message);
            return;
        }
        const check = validateSteps({ steps: resolved.steps });
        if (check) {
            // Spec §7 wording, e.g. "Step 2 is split by score and needs a scored step before it."
            setProblem(describeStepsProblem(check));
            return;
        }
        try {
            await putSteps.mutateAsync(toPutSteps({ steps: resolved.steps }));
            toast.success(isDraft ? "Step added" : "Step saved");
            edit.exitEdit();
            if (isDraft) onDraftDone?.();
        } catch (err) {
            // The card's problem line is for what the page caught, and for
            // the server's field error on this step, with no toast; another
            // step's or the list's is a toast in the page's words.
            if (err instanceof ApiError) {
                const server = serverStepsProblem(err, index);
                if (server && server.index === index) {
                    setProblem(describeStepsProblem(server));
                    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                    return;
                }
                const sibling = server?.index != null ? resolved.steps[server.index]?.stepId : undefined;
                if (sibling) {
                    document
                        .getElementById(SECTION.step(sibling))
                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }
                toastStepsError("Save failed", err);
                return;
            }
            throw err;
        }
    }

    // The re-seed effect puts the form back once the card leaves edit mode;
    // the problem goes here, or the nav's error dot outlives the edit.
    function cancel() {
        setProblem(null);
        edit.exitEdit();
        if (isDraft) onDraftDone?.();
    }

    const n = index + 1;
    const actions = edit.isEditing ? (
        <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={cancel}>
                Cancel
            </Button>
            <Button
                type="button"
                size="sm"
                disabled={putSteps.isPending || (!isDraft && !form.formState.isDirty)}
                onClick={() => void save()}
            >
                Save
            </Button>
        </div>
    ) : edit.isReadOnly ? null : (
        <div className="flex items-center gap-1">
            {!isDraft && (
                <>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Move step ${n} up`}
                        disabled={!onMove || index === 0}
                        onClick={() => onMove?.("up")}
                    >
                        <ChevronUp aria-hidden="true" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Move step ${n} down`}
                        disabled={!onMove || index >= saved.length - 1}
                        onClick={() => onMove?.("down")}
                    >
                        <ChevronDown aria-hidden="true" />
                    </Button>
                </>
            )}
            <Button type="button" variant="outline" size="sm" disabled={stepsSaving} onClick={edit.enterEdit}>
                <Pencil aria-hidden="true" />
                Edit
            </Button>
        </div>
    );

    const follows = row ? splitFollows(saved, index) : null;

    return (
        <SectionCard ref={cardRef} id={sectionId} title={title} actions={actions}>
            {edit.isEditing ? (
                <>
                    <Form form={form}>
                        <StepFields form={form} index={index} rules={rules} />
                    </Form>
                    {scoreMovesFrom !== null && (
                        <p role="status" className="mt-4 text-sm text-kore-text-muted">
                            The score moves off step {scoreMovesFrom} when you save.
                        </p>
                    )}
                    {problem && (
                        <p role="alert" className="mt-4 text-sm text-kore-danger-text">
                            {problem}
                        </p>
                    )}
                    {!isDraft && (
                        <DangerStrip isEditing={edit.isEditing}>
                            <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={() => setConfirmDelete(true)}
                            >
                                Delete step
                            </Button>
                        </DangerStrip>
                    )}
                </>
            ) : (
                row && (
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                            <Badge tone="neutral">{KIND_LABELS[row.kind]}</Badge>
                            {row.isMetric && <Badge tone="accent">Score</Badge>}
                            {row.required && <Badge tone="neutral">Required</Badge>}
                            {follows !== null && <Badge tone="neutral">Follows the score in step {follows + 1}</Badge>}
                        </div>
                        <StepSummary step={row} />
                    </div>
                )
            )}
            {!isDraft && (
                <Confirm
                    variant="destructive"
                    open={confirmDelete}
                    onOpenChange={setConfirmDelete}
                    title={`Delete step ${n}?`}
                    description={
                        row && row.answerCount > 0
                            ? `Step ${n} has ${countOf(row.answerCount, "answer")}; ${row.answerCount === 1 ? "it'll" : "they'll"} be deleted.`
                            : "It has no answers yet."
                    }
                    primaryLabel="Delete step"
                    onConfirm={async () => {
                        const refusal = await onDelete?.();
                        setConfirmDelete(false);
                        if (refusal) setProblem(refusal);
                    }}
                />
            )}
        </SectionCard>
    );
}
